import {
  AlertCircle,
  Bell,
  CheckCircle,
  Mail,
  MessageSquare,
  Send,
  Settings,
  ShieldCheck,
  Sparkles,
} from "lucide-react";

const recentNotifications = [
  {
    id: 1,
    type: "Approval email sent",
    message: "A prepared response for Sarah Mitchell was sent for approval.",
    time: "12 minutes ago",
    icon: Mail,
    color: "text-indigo-600 bg-indigo-100",
    unread: true,
  },
  {
    id: 2,
    type: "Response approved",
    message: "Your response to Michael Johnson was approved and queued.",
    time: "1 hour ago",
    icon: CheckCircle,
    color: "text-green-600 bg-green-100",
    unread: true,
  },
  {
    id: 3,
    type: "Low-rating review detected",
    message: "A 3-star review was flagged for manual review.",
    time: "Yesterday",
    icon: AlertCircle,
    color: "text-orange-600 bg-orange-100",
    unread: false,
  },
  {
    id: 4,
    type: "New review synced",
    message: "Amanda Lewis left a new 5-star review.",
    time: "2 days ago",
    icon: MessageSquare,
    color: "text-blue-600 bg-blue-100",
    unread: false,
  },
];

export default function Notifications() {
  return (
    <div className="space-y-6">
      <header className="flex flex-col xl:flex-row xl:items-center xl:justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl font-bold text-slate-900">
            Notifications
          </h1>

          <p className="text-slate-500 mt-1">
            Control how RevUShield keeps you updated on reviews, approvals, and
            important reputation activity.
          </p>
        </div>

        <button className="border border-slate-200 bg-white hover:bg-slate-50 text-slate-700 px-4 py-2.5 rounded-xl text-sm font-medium transition flex items-center gap-2">
          <Settings size={16} />
          Manage Preferences
        </button>
      </header>

      {/* <section className="grid grid-cols-1 xl:grid-cols-3 gap-5">
        <StatusCard
          icon={<Mail size={22} className="text-indigo-600" />}
          iconBg="bg-indigo-100"
          title="Approval Emails"
          status="Enabled"
          description="Prepared responses are sent to your inbox for quick approval."
        />

        <StatusCard
          icon={<AlertCircle size={22} className="text-orange-600" />}
          iconBg="bg-orange-100"
          title="Low-Rating Alerts"
          status="Enabled"
          description="Reviews rated 3 stars or below are flagged for manual review."
        />

        <StatusCard
          icon={<Clock size={22} className="text-blue-600" />}
          iconBg="bg-blue-100"
          title="Weekly Summary"
          status="Friday mornings"
          description="Receive a digest of review activity and response performance."
        />
      </section> */}

      <section className="grid grid-cols-1 xl:grid-cols-3 gap-6 xl:items-start">
        <div className="xl:col-span-2 space-y-6">
          <section className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden">
            <div className="p-5 border-b border-slate-200">
              <div className="flex items-center gap-2">
                <Bell size={20} className="text-indigo-600" />

                <h2 className="text-lg font-semibold text-slate-900">
                  Notification Preferences
                </h2>
              </div>

              <p className="text-sm text-slate-500 mt-1">
                Choose when RevUShield should notify you or your team.
              </p>
            </div>

            <div className="divide-y divide-slate-200">
              <PreferenceRow
                title="New review alerts"
                description="Notify me when a new Google review is synced."
                enabled
              />

              <PreferenceRow
                title="Approval emails"
                description="Send an email whenever a prepared response needs approval."
                enabled
              />

              <PreferenceRow
                title="Low-rating review alerts"
                description="Immediately flag 1–3 star reviews for manual attention."
                enabled
              />

              <PreferenceRow
                title="Response posted confirmations"
                description="Notify me after an approved response is posted."
                enabled={false}
              />

              <PreferenceRow
                title="Weekly reputation summary"
                description="Send a weekly digest of reviews, approvals, and response activity."
                enabled
              />
            </div>
          </section>

          <section className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden">
            <div className="p-5 border-b border-slate-200 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div className="flex items-center gap-2">
                <Send size={20} className="text-indigo-600" />

                <h2 className="text-lg font-semibold text-slate-900">
                  Recent Delivery Activity
                </h2>
              </div>

              <button className="text-sm font-medium text-indigo-600 hover:text-indigo-700">
                Mark all as read
              </button>
            </div>

            <div className="divide-y divide-slate-200">
              {recentNotifications.map((notification) => {
                const Icon = notification.icon;

                return (
                  <div
                    key={notification.id}
                    className={`p-5 flex gap-4 transition ${
                      notification.unread
                        ? "bg-indigo-50/50"
                        : "bg-white hover:bg-slate-50"
                    }`}
                  >
                    <div
                      className={`h-11 w-11 rounded-xl flex items-center justify-center shrink-0 ${notification.color}`}
                    >
                      <Icon size={20} />
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1">
                        <h3 className="font-semibold text-slate-900">
                          {notification.type}
                        </h3>

                        <span className="text-xs text-slate-400">
                          {notification.time}
                        </span>
                      </div>

                      <p className="text-sm text-slate-600 mt-1">
                        {notification.message}
                      </p>
                    </div>

                    {notification.unread && (
                      <span className="h-2.5 w-2.5 rounded-full bg-indigo-600 mt-2 shrink-0" />
                    )}
                  </div>
                );
              })}
            </div>
          </section>
        </div>

        <aside className="space-y-6 xl:sticky xl:top-24 xl:self-start">
          <section className="rounded-2xl bg-gradient-to-br from-slate-950 via-blue-950 to-indigo-950 text-white p-5 overflow-hidden relative shadow-sm">
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(99,102,241,0.35),transparent_35%)]" />

            <div className="relative">
              <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/10 px-3 py-1 text-xs font-semibold text-indigo-100">
                <Sparkles size={14} />
                Primary workflow
              </div>

              <h2 className="text-xl font-bold mt-5">Approval emails are on</h2>

              <p className="text-sm text-slate-300 mt-2 leading-relaxed">
                Prepared responses are sent directly to your inbox so you can
                approve, edit, or reject without opening the dashboard.
              </p>

              <div className="mt-5 rounded-2xl border border-white/10 bg-white/10 p-4">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-xl bg-green-400/10 text-green-300 flex items-center justify-center">
                    <ShieldCheck size={20} />
                  </div>

                  <div>
                    <p className="text-sm font-semibold text-white">
                      Human approval required
                    </p>

                    <p className="text-xs text-slate-300 mt-1">
                      Nothing posts until you approve it.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section className="bg-white border border-slate-200 rounded-2xl shadow-sm p-5">
            <h2 className="text-lg font-semibold text-slate-900">
              Notification Recipients
            </h2>

            <p className="text-sm text-slate-500 mt-1">
              Approval emails and alerts are currently sent to:
            </p>

            <div className="mt-4 rounded-2xl border border-slate-200 bg-slate-50 p-4">
              <p className="text-sm font-medium text-slate-900">
                owner@joescoffeeshop.com
              </p>

              <p className="text-xs text-slate-500 mt-1">
                Primary business contact
              </p>
            </div>

            <button className="mt-4 w-full border border-slate-200 hover:bg-slate-100 text-slate-700 px-4 py-2.5 rounded-xl text-sm font-medium transition">
              Update Recipients
            </button>
          </section>
        </aside>
      </section>
    </div>
  );
}

// function StatusCard({ icon, iconBg, title, status, description }) {
//   return (
//     <div className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm">
//       <div className="flex items-start justify-between gap-4">
//         <div>
//           <p className="text-sm text-slate-500">{title}</p>

//           <h3 className="text-2xl font-bold text-slate-900 mt-2">{status}</h3>

//           <p className="text-sm text-slate-500 mt-2 leading-relaxed">
//             {description}
//           </p>
//         </div>

//         <div
//           className={`h-11 w-11 rounded-2xl flex items-center justify-center shrink-0 ${iconBg}`}
//         >
//           {icon}
//         </div>
//       </div>
//     </div>
//   );
// }

function PreferenceRow({ title, description, enabled }) {
  return (
    <div className="p-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
      <div>
        <p className="font-medium text-slate-900">{title}</p>

        <p className="text-sm text-slate-500 mt-1">{description}</p>
      </div>

      <button
        className={`h-7 w-12 rounded-full p-1 shrink-0 transition ${
          enabled ? "bg-indigo-600" : "bg-slate-200"
        }`}
      >
        <span
          className={`block h-5 w-5 rounded-full bg-white transition ${
            enabled ? "translate-x-5" : ""
          }`}
        />
      </button>
    </div>
  );
}
